import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { Button, Flex, Input, Pagination, Segmented, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AnalyticsWidgetPagesRequest,
  AnalyticsWidgetStatsRequest,
  WidgetPageStats,
  WidgetPagesSortField,
} from '@shared/api';
import { useGetWidgetPagesQuery } from '@shared/api/widget-pages-api';
import { useDebouncedFunc } from '@shared/hooks/use-debounced-func';
import { Text } from '@shared/ui/antd';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { Table } from '@shared/ui/table';

import {
  DEFAULT_MIN_VISITORS,
  DEFAULT_PAGES_SORT,
  MIN_VISITORS_OPTIONS,
  NO_ESCAPE,
  PAGES_PAGE_SIZE,
  type MinVisitorsOption,
  type PagesSort,
  formatCount,
  formatNumber,
  formatPotential,
  formatRate,
  getDeltaPercent,
  getPreviewStatus,
  nextSort,
} from '../../lib/widget-pages';
import { WidgetPageDrawer } from './widget-page-drawer';
import { DeltaBadge, PreviewStatusTag, RateCell } from './widget-pages-bits';
import styles from './widget-pages.module.scss';

type Props = {
  request: AnalyticsWidgetStatsRequest | null;
  enabled: boolean;
};

type Filters = {
  hosts: string[];
  minVisitors: MinVisitorsOption;
  search: string;
  sort: PagesSort;
};

const DEFAULT_FILTERS: Filters = {
  hosts: [],
  minVisitors: DEFAULT_MIN_VISITORS,
  search: '',
  sort: DEFAULT_PAGES_SORT,
};

const SEARCH_DEBOUNCE_MS = 400;

export const WidgetPages: FC<Props> = ({ request, enabled }) => {
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'analytics.widget.pages' });
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState<WidgetPageStats | null>(null);

  // The page number is tied to the filters it was chosen for, so any filter or period
  // change starts from the first page without an extra render or request.
  const filtersKey = useMemo(() => JSON.stringify([request, filters]), [request, filters]);
  const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
  const page = pageState.key === filtersKey ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ key: filtersKey, page: next });

  const updateFilters = (patch: Partial<Filters>) =>
    setFilters((current) => ({ ...current, ...patch }));
  const applySearch = useDebouncedFunc(
    (value: string) => updateFilters({ search: value.trim() }),
    SEARCH_DEBOUNCE_MS,
  );

  const args = useMemo<AnalyticsWidgetPagesRequest | typeof skipToken>(() => {
    if (!request || !enabled) return skipToken;
    return {
      ...request,
      hosts: filters.hosts.length ? filters.hosts : null,
      min_visitors: filters.minVisitors,
      search: filters.search || null,
      sort_by: filters.sort.field,
      sort_desc: filters.sort.desc,
      limit: PAGES_PAGE_SIZE,
      offset: (page - 1) * PAGES_PAGE_SIZE,
    };
  }, [enabled, filters, page, request]);

  const { data, isLoading, isFetching, isError, refetch } = useGetWidgetPagesQuery(args);

  const hostOptions = data?.hosts ?? [];
  const showHostColumn = hostOptions.length > 1;
  const isFiltered =
    filters.hosts.length > 0 ||
    filters.search.length > 0 ||
    filters.minVisitors !== DEFAULT_MIN_VISITORS;
  const total = data?.total_pages ?? 0;
  const rangeFrom = total === 0 ? 0 : (page - 1) * PAGES_PAGE_SIZE + 1;
  const rangeTo = Math.min(page * PAGES_PAGE_SIZE, total);
  const locale = i18n.language;

  const toggleHost = (host: string, checked: boolean) => {
    updateFilters({
      hosts: checked
        ? [...filters.hosts, host]
        : filters.hosts.filter((current) => current !== host),
    });
  };

  const resetFilters = () => {
    setSearchInput('');
    applySearch.cancel();
    setFilters(DEFAULT_FILTERS);
  };

  const onSort = (field: WidgetPagesSortField) =>
    updateFilters({ sort: nextSort(filters.sort, field) });

  const sortableHeader = (field: WidgetPagesSortField, width: number, label: React.ReactNode) => (
    <Table.THeadCellSortable
      width={width}
      isSorted={filters.sort.field === field}
      isDesc={filters.sort.desc}
      onSort={() => onSort(field)}
    >
      {label}
    </Table.THeadCellSortable>
  );

  const benchmarkTooltip = (item: WidgetPageStats) => {
    if (item.benchmark_lead_rate === null || item.benchmark_lead_rate === undefined) {
      return t('benchmarkTooltip.none');
    }
    const level = item.benchmark_level ?? 'all';
    return t(`benchmarkTooltip.${level}`, {
      segment: level === 'host' ? item.page_host : item.segment,
      rate: formatRate(item.benchmark_lead_rate),
      ...NO_ESCAPE,
    });
  };

  const potentialTooltip = (item: WidgetPageStats) => {
    if (item.benchmark_lead_rate === null || item.benchmark_lead_rate === undefined) {
      return t('potentialTooltip.none');
    }
    const level = item.benchmark_level ?? 'all';
    const scope = t(`potentialTooltip.scope.${level}`, {
      segment: level === 'host' ? item.page_host : item.segment,
      ...NO_ESCAPE,
    });
    const benchmark = formatRate(item.benchmark_lead_rate);
    if (item.potential_leads <= 0) {
      return t('potentialTooltip.atBenchmark', { benchmark, scope, ...NO_ESCAPE });
    }
    return t('potentialTooltip.formula', {
      visitors: formatCount(item.visitors, locale),
      benchmark,
      rate: formatRate(item.lead_rate),
      result: formatNumber(item.potential_leads),
      scope,
      ...NO_ESCAPE,
    });
  };

  const renderBody = () => {
    if (isLoading) return <LoadingState />;
    if (isError) return <FetchingErrorState refetch={refetch} />;
    if (!data) return null;

    if (data.items.length === 0) {
      return (
        <div className={styles.empty}>
          <Text variant="sm">{isFiltered ? t('emptyFiltered') : t('empty')}</Text>
          {isFiltered && (
            <Button type="link" size="small" onClick={resetFilters}>
              {t('resetFilters')}
            </Button>
          )}
        </div>
      );
    }

    return (
      <>
        <div className={styles.tableWrap} data-fetching={isFetching}>
          <Table maxHeight="720px">
            <Table.THead isSticky>
              {sortableHeader('page', 360, t('columns.page'))}
              {sortableHeader('visitors', 150, t('columns.visitors'))}
              {sortableHeader('chat_rate', 120, t('columns.chat'))}
              {sortableHeader('message_rate', 120, t('columns.messages'))}
              {sortableHeader('leads', 120, t('columns.leads'))}
              {sortableHeader(
                'potential_leads',
                140,
                <span className={styles.headerWithHint}>
                  {t('columns.potential')}
                  <Tooltip
                    title={
                      <div className={styles.hintBlock}>
                        <p>{t('potentialHint')}</p>
                        <p>{t('similarPagesHint', { count: data.low_data_visitors })}</p>
                      </div>
                    }
                  >
                    <InfoCircleOutlined className={styles.hintIcon} />
                  </Tooltip>
                </span>,
              )}
              <Table.THeadCellText width={120}>
                <span className={styles.headerWithHint}>
                  {t('columns.preview')}
                  {data.preview_mode === 'static' && (
                    <Tooltip title={t('previewStaticHint')}>
                      <InfoCircleOutlined className={styles.hintIcon} />
                    </Tooltip>
                  )}
                </span>
              </Table.THeadCellText>
            </Table.THead>

            <Table.TBody>
              {data.items.map((item) => (
                <Table.TRow
                  key={item.url}
                  hasHover
                  onClick={() => setSelected(item)}
                  className={classNames(styles.row, { [styles.rowMuted]: item.low_data })}
                >
                  <Table.TCell width="auto">
                    <div className={styles.pageCell}>
                      <span className={styles.pagePath} title={item.url}>
                        {item.page_path}
                      </span>
                      {showHostColumn && <span className={styles.pageHost}>{item.page_host}</span>}
                    </div>
                  </Table.TCell>

                  <Table.TCell width="auto">
                    <span className={styles.rateCell}>
                      <span className={styles.value}>{formatCount(item.visitors, locale)}</span>
                      <DeltaBadge
                        delta={getDeltaPercent(item.visitors, item.previous?.visitors)}
                        tooltip={
                          item.previous
                            ? t('previousPeriod', {
                                value: formatCount(item.previous.visitors, locale),
                              })
                            : undefined
                        }
                      />
                    </span>
                  </Table.TCell>

                  <Table.TCell width="auto">
                    <RateCell rate={item.chat_rate} count={item.chat_open} />
                  </Table.TCell>

                  <Table.TCell width="auto">
                    <RateCell rate={item.message_rate} count={item.first_message} />
                  </Table.TCell>

                  <Table.TCell width="auto">
                    <Tooltip title={benchmarkTooltip(item)}>
                      <span className={styles.rateCell}>
                        <span className={styles.value}>{item.leads}</span>
                        <span className={styles.secondary}>{formatRate(item.lead_rate)}</span>
                      </span>
                    </Tooltip>
                  </Table.TCell>

                  <Table.TCell width="auto">
                    {item.low_data ? (
                      <Tooltip title={t('lowData', { count: data.low_data_visitors })}>
                        <span className={styles.lowData}>{t('lowDataShort')}</span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={potentialTooltip(item)}>
                        <span
                          className={classNames(styles.value, styles.explained, {
                            [styles.potential]: item.potential_leads > 0,
                          })}
                        >
                          {formatPotential(item.potential_leads)}
                        </span>
                      </Tooltip>
                    )}
                  </Table.TCell>

                  <Table.TCell width="auto">
                    <PreviewStatusTag status={getPreviewStatus(item, data.preview_mode)} />
                  </Table.TCell>
                </Table.TRow>
              ))}
            </Table.TBody>
          </Table>
        </div>

        <div className={styles.footer}>
          <span className={styles.secondary}>
            {t('range', { from: rangeFrom, to: rangeTo, total: formatCount(total, locale) })}
          </span>
          {total > PAGES_PAGE_SIZE && (
            <Pagination
              size="small"
              current={page}
              pageSize={PAGES_PAGE_SIZE}
              total={total}
              showSizeChanger={false}
              onChange={setPage}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <Flex align="center" gap={6}>
          <Text variant="xl" fontWeight={500}>
            {t('title')}
          </Text>
          <Tooltip title={t('hint')}>
            <InfoCircleOutlined className={styles.hintIcon} />
          </Tooltip>
        </Flex>

        {data && data.totals.pages > 0 && (
          <span className={styles.summary}>
            {t('summary', {
              pages: formatCount(data.totals.pages, locale),
              visitors: formatCount(data.totals.visitors, locale),
              leads: formatCount(data.totals.leads, locale),
              rate: formatRate(data.totals.lead_rate),
            })}
            {data.previous_period_available === false && (
              <Tooltip title={t('noPreviousPeriodHint')}>
                <span className={styles.summaryNote}>{t('noPreviousPeriod')}</span>
              </Tooltip>
            )}
          </span>
        )}
      </div>

      <div className={styles.controls}>
        {hostOptions.length > 1 && (
          <div className={styles.chips}>
            <Tag.CheckableTag
              checked={filters.hosts.length === 0}
              onChange={() => updateFilters({ hosts: [] })}
            >
              {t('allHosts')}
            </Tag.CheckableTag>
            {hostOptions.map((host) => (
              <Tag.CheckableTag
                key={host.host}
                checked={filters.hosts.includes(host.host)}
                onChange={(checked) => toggleHost(host.host, checked)}
              >
                {host.host}
                <span className={styles.chipCount}>{formatCount(host.visitors, locale)}</span>
              </Tag.CheckableTag>
            ))}
          </div>
        )}

        <div className={styles.controlsRight}>
          <Tooltip title={t('minVisitors.hint')}>
            <Segmented
              size="small"
              value={filters.minVisitors}
              onChange={(value) => updateFilters({ minVisitors: value as MinVisitorsOption })}
              options={MIN_VISITORS_OPTIONS.map((value) => ({
                value,
                label: value === 0 ? t('minVisitors.all') : `${value}+`,
              }))}
            />
          </Tooltip>

          <Input
            allowClear
            size="small"
            className={styles.search}
            prefix={<SearchOutlined className={styles.searchIcon} />}
            placeholder={t('search')}
            aria-label={t('search')}
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              applySearch(event.target.value);
            }}
          />
        </div>
      </div>

      {renderBody()}

      <WidgetPageDrawer
        page={selected}
        request={request}
        previewMode={data?.preview_mode}
        lowDataVisitors={data?.low_data_visitors ?? 0}
        onClose={() => setSelected(null)}
      />
    </section>
  );
};
